import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { stopScenarioRun } from '../../api/scenarioStop';
import { suiteFailureMessage } from '../../api/scenarioSuites';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';

export interface IStopRunButtonProps {
    customer: string;
    environment: string;
    runId: string;
    // A smaller button, for a row in the run list.
    compact?: boolean;
    onStopped?: () => void;
}

/**
 * Stops a run the dashboard is carrying, after asking once.
 *
 * Stopping cannot be undone: the scenario that is going is skipped and nothing after it runs. The run does put the
 * configuration from before the run back on its way out, which is what the question says, so nobody hesitates
 * over leaving the environment half configured. Once asked, the button stays in its stopping state until the page
 * learns the run has ended; the API only promises that the stop has been asked for.
 */
export const StopRunButton = ({
    customer,
    environment,
    runId,
    compact,
    onStopped,
}: IStopRunButtonProps) => {
    const axios = useAxiosSwagger();
    const [stopping, setStopping] = useState(false);
    const [error, setError] = useState('');

    const stop = async () => {
        if (!axios) return;

        if (
            !window.confirm(
                'Stop this run? The scenario that is going is skipped and nothing after it runs. The configuration from before the run is restored before the run ends.'
            )
        ) {
            return;
        }

        setStopping(true);
        setError('');

        try {
            await stopScenarioRun(axios, customer, environment, runId);
            onStopped?.();
        } catch (failure) {
            setStopping(false);
            setError(suiteFailureMessage(failure, 'The run could not be stopped.'));
        }
    };

    return (
        <span className="arl-stop">
            <button
                type="button"
                className={`btn btn-sm btn-outline-danger${compact ? ' arl-stop__compact' : ''}`}
                disabled={stopping}
                title={
                    stopping
                        ? 'Stopping: the scenario that was going is skipped and the configuration is being restored'
                        : 'Stop this run and restore the configuration from before it'
                }
                onClick={stop}
            >
                <FontAwesomeIcon
                    icon={stopping ? 'spinner' : 'circle-stop'}
                    spin={stopping}
                    className="me-1"
                />
                {stopping ? 'Stopping' : 'Stop'}
            </button>
            {error && <span className="arl-stop__error">{error}</span>}
        </span>
    );
};
